import { useEffect, useMemo, useState } from "react";
import { useChatStore } from "../stores/ChatStore";
import debounce from "lodash.debounce";
import { ChatGroupRoom, ListChatGroupRoom } from "../interfaces/Chat/Chat";

export const useChatGroupSearch = (search: string, selectedFilterLabel: string, selectedFilterStatus: string) => {
    const { listChatGroupRooms, listChatGroupMessages, groupMessageNotifications } = useChatStore();
    const [searchResults, setSearchResults] = useState<ListChatGroupRoom>({});
    const [filteredResults, setFilteredResults] = useState<ListChatGroupRoom>({});

    const chatRoomsArray = useMemo(() => Object.values(listChatGroupRooms), [listChatGroupRooms]);
    const chatMessagesArray = useMemo(() => Object.entries(listChatGroupMessages), [listChatGroupMessages]);

    /*** Filter Chat Berdasarkan Filter ***/
    const applyFilters = (chat: ChatGroupRoom) => {
        const matchesLabel = !selectedFilterLabel || chat.labels?.some(label => label.id === selectedFilterLabel);

        const isUnread = groupMessageNotifications[`${chat.groupId}|${chat.as}`];
        const matchesStatus = !selectedFilterStatus || (selectedFilterStatus === "unread" && isUnread && isUnread.length > 0) || (selectedFilterStatus === "read" && !isUnread);
        // const matchesStatus = !selectedFilterStatus;

        return matchesLabel && matchesStatus;
    };

    /*** Proses Search ***/
    const searchChats = useMemo(() => {
        return debounce((searchText: string) => {
            if (!searchText) {
                setSearchResults({});
                return;
            }

            let searchMessage = searchText.toLowerCase();
            let searchContact = searchText.toLowerCase();

            if (searchContact.length >= 2 && searchContact.startsWith("08")) {
                searchContact = "628" + searchContact.substring(2);
            }

            const tempResults: ListChatGroupRoom = {};

            // Search berdasarkan nama dan nomor telepon
            chatRoomsArray.forEach((chat: ChatGroupRoom) => {
                if (chat.groupName.toLowerCase().includes(searchContact) || chat.participants.some(member => member.name?.toLowerCase().includes(searchContact))) {
                    const key = `${chat.groupId}|${chat.as}|contact`;
                    tempResults[key] = { ...chat };
                }
            });

            // Search berdasarkan isi pesan (mencari semua pesan yang cocok)
            chatMessagesArray.forEach(([key, messages]) => {
                const room = listChatGroupRooms[key];

                messages.forEach((msg, index) => {
                    if (msg.message.toLowerCase().includes(searchMessage)) {
                        if (room) {
                            const uniqueKey = `${key}|message|${msg.id}`; // Membuat key unik berdasarkan index pesan
                            tempResults[uniqueKey] = {
                                ...room,
                                chat: msg, // Hanya menyimpan pesan yang cocok
                            };
                        }
                    }
                });
            });

            setSearchResults(tempResults);
        }, 300);
    }, [chatRoomsArray, chatMessagesArray]);

    /*** Proses Filter ***/
    useEffect(() => {
        let dataToFilter = search ? searchResults : listChatGroupRooms;
        const filtered = Object.keys(dataToFilter).reduce((acc: ListChatGroupRoom, key) => {
            const chat = dataToFilter[key] as ChatGroupRoom;
            if (applyFilters(chat)) {
                if (search) acc[key] = chat;
                else acc[key + '|contact'] = chat;
            }

            return acc;
        }, {});
        setFilteredResults(filtered);
    }, [searchResults, search, selectedFilterLabel, selectedFilterStatus]);

    /*** Jalankan Search Saat Search Berubah ***/
    useEffect(() => {
        searchChats(search);
        return () => searchChats.cancel();
    }, [search, searchChats]);

    return filteredResults;
};
